import React, { useState } from "react";
import { useCashbookStatus } from "../../../../helpers/cashbookStatus";
import { approveCashChange, rejectCashChange } from "../../../../services/cashBookApi";
import { T, useKT } from "../../i18n/kt";
import { rupees } from "../../rates/rateModel";
import { readRole } from "../CashbookGuard";
import { num } from "../money";
import type { ChangeItem, DecideResponse } from "../types";
import { effectLine, isOwnerRole, ownerRefusal, reasonWord, signedRupees } from "./ownerText";
import "./owner.scss";

/**
 * What approving would do, before anyone decides (D17). The holder's name comes back with the decision;
 * before it, the line says what moves. A sheet row or a payment going down puts cash back in the hand
 * that paid it and leaves the worker that much more due.
 */
export const approvePreview = (c: Pick<ChangeItem, "inForce" | "newAmount" | "voidEntry" | "recordType" | "person">, t: T): string => {
  const next = c.voidEntry || c.newAmount === null ? 0 : num(c.newAmount);
  const back = Math.round((num(c.inForce) - next) * 100) / 100;
  if (back === 0) return "";
  const parts = [t("Approve: cash in hand {{delta}}", { delta: signedRupees(back) })];
  if (c.person && c.recordType !== "CASH_ENTRY") {
    parts.push(back > 0 ? t("{{name}} {{amount}} more due", { name: c.person.name, amount: rupees(back) }) : t("{{name}} {{amount}} less due", { name: c.person.name, amount: rupees(-back) }));
  }
  return parts.join(" · ");
};

export const changeTitle = (c: ChangeItem, t: T): string => {
  const who = c.requestedBy?.name || "";
  const forWhom = c.person ? c.person.name : "";
  const sheet = c.sheetNo ? ` (${c.sheetNo})` : "";
  if (c.voidEntry || c.newAmount === null)
    return forWhom ? t("Change: {{name}} wants to remove {{amount}} for {{person}}{{sheet}}.", { name: who, amount: rupees(c.inForce), person: forWhom, sheet }) : t("Change: {{name}} wants to remove {{amount}}{{sheet}}.", { name: who, amount: rupees(c.inForce), sheet });
  return forWhom
    ? t("Change: {{name}} wants {{old}} → {{new}} for {{person}}{{sheet}}.", { name: who, old: rupees(c.inForce), new: rupees(c.newAmount), person: forWhom, sheet })
    : t("Change: {{name}} wants {{old}} → {{new}}{{sheet}}.", { name: who, old: rupees(c.inForce), new: rupees(c.newAmount), sheet });
};

type Props = { change: ChangeItem; onDecided?: (r: DecideResponse) => void };

/**
 * Approve or keep one change request (V1_SPEC 7.7, 11.6 L5/L6, D15, D16). "Why?" is required for both.
 * The requester never decides it; the owner may "Approve anyway" with a reason, marked self-approved.
 */
const ChangeDecision = ({ change, onDecided }: Props) => {
  const { t } = useKT();
  const status = useCashbookStatus();
  const owner = isOwnerRole(readRole());
  const [why, setWhy] = useState("");
  const [busy, setBusy] = useState<null | "approve" | "reject">(null);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<DecideResponse | null>(null);

  const removal = change.voidEntry || change.newAmount === null;
  const blockedFor = change.canDecide === "YOU_ASKED" ? (owner ? "override" : "blocked") : change.canDecide === "YOUR_OWN_PAY" ? "blocked" : null;

  const decide = async (kind: "approve" | "reject") => {
    if (!why.trim()) return;
    setBusy(kind);
    setError(null);
    try {
      const body = { reason: why.trim(), override: blockedFor === "override" ? { reason: why.trim() } : null };
      const res = kind === "approve" ? await approveCashChange(change.proposalSeq, body) : await rejectCashChange(change.proposalSeq, body);
      if (!res?.success || !res.data) {
        setError(ownerRefusal(res, t));
        return;
      }
      setResult(res.data);
      status.refresh();
      onDecided?.(res.data);
    } catch {
      setError(t("Not saved. Check the network and retry."));
    } finally {
      setBusy(null);
    }
  };

  if (result) {
    const approved = result.decision === "APPROVED";
    const effect = approved ? effectLine(result.cashEffect, change.person?.name || null, t) : "";
    return (
      <div className="cbo-decide" role="status" aria-live="polite">
        <div className="cb-card is-ok">
          <p>
            {approved
              ? removal
                ? t("Removed. {{amount}} stays in the record.", { amount: rupees(change.inForce) })
                : t("{{new}} is now in force. {{old}} stays in the record.", { new: rupees(change.newAmount), old: rupees(change.inForce) })
              : t("{{amount}} stays in force.", { amount: rupees(change.inForce) })}
          </p>
          {effect ? <p>{effect}</p> : null}
        </div>
      </div>
    );
  }

  const preview = approvePreview(change, t);
  const asked = change.reason ? `"${change.reason}"` : reasonWord(change.reasonCode, t);

  return (
    <div className="cbo-decide">
      <p className="cbo-pay__line">
        <s>{rupees(change.inForce)}</s> <strong>{removal ? t("remove") : rupees(change.newAmount)}</strong>
        <span className="cbo-chip is-doubt">{t("change pending")}</span>
      </p>
      <p className="cbo-decide__effect">
        {t("{{name}} asked", { name: change.requestedBy?.name || "" })}: {asked}
      </p>
      {preview ? <p className="cbo-decide__effect">{preview}</p> : null}

      {blockedFor === "blocked" ? (
        <p className="cbo-row__own">
          {change.canDecide === "YOUR_OWN_PAY" ? t("This is your own pay. Someone else decides it.") : t("{{name}} asked for this change, so someone else decides it.", { name: change.requestedBy?.name || "" })}
        </p>
      ) : (
        <>
          {blockedFor === "override" ? (
            <p className="cbo-row__own">{t("{{name}} asked for this change, so someone else decides it.", { name: change.requestedBy?.name || "" })} {t("As the owner you may decide anyway; it is marked self-approved.")}</p>
          ) : null}
          <label className="cb-field">
            <span className="cb-field__label">{t("Why?")}</span>
            <textarea rows={2} value={why} onChange={(e) => setWhy(e.target.value)} placeholder={t("e.g. Checked with Mohan")} />
          </label>
          <div className="cbo-decide__btns">
            <button type="button" className="kh-btn is-primary" disabled={!why.trim() || !!busy} onClick={() => decide("approve")}>
              {busy === "approve"
                ? t("Saving…")
                : blockedFor === "override"
                  ? t("Approve anyway")
                  : removal
                    ? t("Approve removal")
                    : t("Approve {{amount}}", { amount: rupees(change.newAmount) })}
            </button>
            <button type="button" className="kh-btn" disabled={!why.trim() || !!busy} onClick={() => decide("reject")}>
              {busy === "reject" ? t("Saving…") : t("Keep {{amount}}", { amount: rupees(change.inForce) })}
            </button>
          </div>
          {!why.trim() ? <p className="cb-muted">{t("Say why to approve or keep.")}</p> : null}
        </>
      )}
      {error ? (
        <p className="cbo-row__err" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
};

export default ChangeDecision;
